import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { REPORT_FIELD } from "../../lib/directoryLabels";
import { useDeviceLocation } from "./LocationSearch";

export default function ReportDialog({ agency }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ fields: [], comment: "", email: "", suggested: null });
  const [busy, setBusy] = useState(false);
  const device = useDeviceLocation();
  const toggle = (k) => setF({ ...f, fields: f.fields.includes(k) ? f.fields.filter((x) => x !== k) : [...f.fields, k] });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/public/directory/agencies/${agency.id}/report`, { fields: f.fields, comment: f.comment, email: f.email,
        suggested_latitude: f.suggested?.latitude ?? null, suggested_longitude: f.suggested?.longitude ?? null });
      toast.success("Merci, votre signalement a été transmis pour vérification");
      setOpen(false); setF({ fields: [], comment: "", email: "", suggested: null });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => setOpen(true)} data-testid="agency-report-button"><Flag className="mr-1.5 h-3.5 w-3.5" />Signaler une information incorrecte</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="agency-report-dialog">
          <DialogHeader>
            <DialogTitle>Signaler une information incorrecte</DialogTitle>
            <DialogDescription>{agency.name} — votre signalement est transmis à l'organisation et vérifié par l'équipe VOXA AFRIKA.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(REPORT_FIELD).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={f.fields.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`report-field-${k}`} />{label}</label>
              ))}
            </div>
            <Textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} placeholder="Décrivez ce qui est incorrect (min. 5 caractères)" rows={3} data-testid="report-comment" />
            {f.fields.includes("position") && (
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="text-slate-600">Vous êtes sur place ? Proposez votre position actuelle (facultatif).</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={device.busy} onClick={() => device.locate((p) => setF((x) => ({ ...x, suggested: p })))} data-testid="report-use-device">Utiliser ma position</Button>
                  {f.suggested && <span className="font-mono text-xs" data-testid="report-suggested">{f.suggested.latitude}, {f.suggested.longitude}</span>}
                </div>
              </div>
            )}
            <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Votre email (facultatif, jamais publié)" data-testid="report-email" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={busy || !f.fields.length || f.comment.trim().length < 5} data-testid="report-submit">
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Envoyer le signalement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
